import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Search } from '@/components/icons';
import { listAuditLogs, getAuditFacets, type AuditLogRow } from '@/api/audit';

type Tone = 'success' | 'danger' | 'warning' | 'info' | 'accent' | 'muted';
function actionTone(a: string): Tone {
  if (/delete|remove|reject|cancel|block|revoke/i.test(a)) return 'danger';
  if (/create|add|approve|save|resolve|grant|activate/i.test(a)) return 'success';
  if (/update|edit|set|change|map|assign/i.test(a)) return 'info';
  return 'muted';
}

function dt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Friendly module name for an audit entity type (the module the action touched).
const MODULE_LABELS: Record<string, string> = {
  enquiry: 'Order Review / Sales',
  dispatch_order: 'Dispatch Order',
  pick_list: 'Pick List',
  packing: 'Packing',
  shopify_order: 'Online Orders',
  shipment: 'Shipping',
  outward_grn: 'Dispatch Note',
  sales_invoice: 'Invoice',
  gate_pass: 'Gate Pass',
  sales_return: 'Sales Return',
  sis_stock: 'SIS',
  sis_settlement: 'SIS Settlement',
  warehouse_stock: 'Warehouse Stock',
  item: 'Item Master',
  party: 'Party Master',
  price_master: 'Price Master',
  location: 'Location Master',
  user: 'User Management',
  role: 'Role Management',
  mrn: 'MRN',
  pr: 'Purchase Req.',
  po: 'Purchase Orders',
  grn: 'GRN',
  rfq: 'Quotations / RFQ',
  quote_approval: 'Quote Approval',
  auth: 'Authentication',
};
function moduleLabel(entityType: string): string {
  return MODULE_LABELS[entityType] ?? entityType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AuditLogsPage() {
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [detail, setDetail] = useState<AuditLogRow | null>(null);

  // Debounce the search box into the applied query.
  useEffect(() => {
    const t = setTimeout(() => setApplied(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  // Any filter change → back to page 1.
  useEffect(() => { setPage(1); }, [applied, action, entityType, from, to, pageSize]);

  const facetsQ = useQuery({ queryKey: ['audit-facets'], queryFn: getAuditFacets, staleTime: 300_000 });
  const filter = {
    search: applied || undefined,
    action: action || undefined,
    entityType: entityType || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize,
  };
  const query = useQuery({ queryKey: ['audit', filter], queryFn: () => listAuditLogs(filter) });
  const data = query.data;
  const rows = data?.rows ?? [];

  return (
    <div>
      <SectionHeader
        title="Audit Logs"      />

      <FilterBar>
        <FilterField label="Search" className="sm:w-[250px]">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="User, action or entity" className="!pl-8" />
          </div>
        </FilterField>
        <FilterField label="Action" className="sm:w-[180px]">
          <Select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {(facetsQ.data?.actions ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Entity" className="sm:w-[180px]">
          <Select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            <option value="">All entities</option>
            {(facetsQ.data?.entityTypes ?? []).map((e) => <option key={e} value={e}>{e}</option>)}
          </Select>
        </FilterField>
        <FilterField label="From" className="sm:w-[150px]"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></FilterField>
        <FilterField label="To" className="sm:w-[150px]"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></FilterField>
      </FilterBar>

      <Table<AuditLogRow>
        storageKey="audit-logs"
        title="Activity"
        count={`${data?.total ?? 0} events`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        empty="No audit events match these filters."
        footer={<Pagination page={page} pageSize={pageSize} total={data?.total ?? 0} setPage={setPage} setPageSize={setPageSize} />}
        columns={[
          { key: 'at', header: 'Time', width: '185px', cell: (r) => <span className="whitespace-nowrap">{dt(r.at)}</span> },
          { key: 'user', header: 'User', width: '170px', cell: (r) => (r.actorName ? <span className="font-semibold">{r.actorName}</span> : <span className="text-[var(--text-muted)]">System</span>) },
          { key: 'action', header: 'Action', width: '150px', align: 'center', cell: (r) => <Badge tone={actionTone(r.action)}>{r.action}</Badge> },
          { key: 'module', header: 'Module', width: '170px', cell: (r) => <span className="font-medium">{moduleLabel(r.entityType)}</span> },
          { key: 'entity', header: 'Record', width: '170px', cell: (r) => (
            r.ref
              ? <span className="font-mono text-xs font-semibold text-[var(--text-primary)]">{r.ref}</span>
              : <span className="text-[var(--text-muted)]">{r.entityId ? `#${r.entityId}` : '—'}</span>
          ) },
          { key: 'detail', header: 'Details', width: '110px', align: 'right', cell: (r) => ((r.beforeJson || r.afterJson) ? <Button variant="glass" size="sm" onClick={() => setDetail(r)}>View</Button> : <span className="text-[var(--text-muted)]">—</span>) },
        ]}
      />

      <Modal
        open={detail != null}
        onClose={() => setDetail(null)}
        size="2xl"
        title={detail ? `${detail.action} · ${moduleLabel(detail.entityType)} · ${detail.ref ?? (detail.entityId ? `#${detail.entityId}` : '')}` : 'Detail'}
      >
        {detail && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="When" value={dt(detail.at)} />
              <Field label="User" value={detail.actorName ?? 'System'} />
              <Field label="Action" value={detail.action} />
              <Field label="Module" value={moduleLabel(detail.entityType)} />
            </div>
            {detail.beforeJson && <JsonBlock label="Before" json={detail.beforeJson} />}
            {detail.afterJson && <JsonBlock label="After" json={detail.afterJson} />}
            {!detail.beforeJson && !detail.afterJson && <div className="text-xs text-[var(--text-muted)]">No before/after snapshot recorded for this action.</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function JsonBlock({ label, json }: { label: string; json: string }) {
  let pretty = json;
  try { pretty = JSON.stringify(JSON.parse(json), null, 2); } catch { /* keep raw */ }
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <pre className="max-h-64 overflow-auto rounded-card border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 text-[11px] leading-relaxed text-[var(--text-secondary)]">{pretty}</pre>
    </div>
  );
}
